import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { ITagCombination } from '../../../../view/settings/api-automation-settings/tag-combinations/tag-combinations.model';
import { TagCombinationService } from './tag-combinations.service';
import {
  ActionTypes,
  TagCombinationsLoading,
  TagCombinationsLoaded,
  FetchError,
  GetTagCombination,
  GetTagCombinationCompleted,
  EditTagCombination,
  EditTagCombinationCompleted,
  AddTagCombination,
  AddTagCombinationCompleted,
  DeleteTagCombinations,
  DeleteTagCombinationsCompleted,
  DestinationsDataLoading,
  DestinationsDataCompleted,
  HeartbeatSettingsDataLoading,
  HeartbeatSettingsDataCompleted,
  DestinationMapDataLoading,
  DestinationMapDataCompleted,
} from './tag-combinations.actions';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { EApiAutomationResource } from '../shared/api-automation.model';

@Injectable()
export class TagCombinationEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: TagCombinationService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly lookupService: ApiAutomationLookupService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.TAG_COMBINATIONS_DATA_LOADING),
      switchMap((payload: TagCombinationsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [
              { tagCombinations: { $cont: searchText } },
              { description: { $cont: searchText } },
              { tagType: { $cont: searchText } },
            ],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new TagCombinationsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_TAG_COMBINATION),
      switchMap((objectData: GetTagCombination) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<ITagCombination>) =>
            of(new GetTagCombinationCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_TAG_COMBINATION),
      switchMap((objectData: AddTagCombination) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ITagCombination>) =>
            of(
              new AddTagCombinationCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_TAG_COMBINATION),
      switchMap((objectData: EditTagCombination) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ITagCombination>) =>
            of(
              new EditTagCombinationCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_TAG_COMBINATIONS),
      switchMap((objectData: DeleteTagCombinations) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteTagCombinationsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getDestinationsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DESTINATIONS_DATA_LOADING),
      switchMap((objectData: DestinationsDataLoading) =>
        this.lookupService.getTagCombinationDestinationOptions(objectData.tagType).pipe(
          switchMap((response) => of(new DestinationsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getHeartbeatSettingsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.HEARTBEAT_SETTINGS_DATA_LOADING),
      switchMap(() =>
        this.lookupService.getOptions(EApiAutomationResource.heartbeatSettings).pipe(
          switchMap((response) => of(new HeartbeatSettingsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getDestinationMapData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DESTINATION_MAP_DATA_LOADING),
      switchMap((objectData: DestinationMapDataLoading) =>
        this.lookupService.getDestinationOptionsMap(objectData.tagTypes, true).pipe(
          switchMap((response) => of(new DestinationMapDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
